import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { getAdminTemplateFieldTypes } from "@/lib/fieldTypes";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";

interface FieldTemplate {
  id: number;
  template_key: string;
  scope: string;
  semantic_role: string;
  field_type: string;
  default_label: string;
  default_validation_json: unknown;
  default_options_json: unknown;
  default_ui_json: unknown;
  default_behavior_json: unknown;
  category: string | null;
  icon: string | null;
  description: string | null;
  version: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface CreatePayload {
  field_type: string;
  version: number;
  description: string | null;
  is_active: boolean;
}

type Feedback = { type: "info" | "success" | "warning" | "error"; message: string } | null;

const DEFAULT_FORM = {
  field_type: "text",
  version: 1,
  description: "",
  is_active: true,
};

export function FieldTemplatesPage() {
  const [templates, setTemplates] = useState<FieldTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const fieldTypes = useMemo(() => getAdminTemplateFieldTypes(), []);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await api.get<FieldTemplate[]>("/field-templates");
      setTemplates(Array.isArray(rows) ? rows : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unbekannter Fehler";
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function resetForm() {
    setForm({ ...DEFAULT_FORM });
    setFeedback({ type: "warning", message: "Formular zurückgesetzt." });
  }

  function loadTemplateIntoForm(t: FieldTemplate) {
    setForm({
      field_type: t.field_type || "text",
      version: Number(t.version || 1),
      description: t.description || "",
      is_active: Boolean(t.is_active),
    });
    setFeedback({ type: "success", message: "Template in Formular geladen." });
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback({ type: "warning", message: "Template wird gespeichert …" });
    setSubmitting(true);
    try {
      const payload: CreatePayload = {
        field_type: form.field_type.trim(),
        version: Number(form.version) || 1,
        description: form.description.trim() ? form.description.trim() : null,
        is_active: form.is_active,
      };
      await api.post<FieldTemplate>("/field-templates", payload);
      setForm({ ...DEFAULT_FORM });
      setFeedback({ type: "success", message: "Template gespeichert." });
      await reload();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Speichern fehlgeschlagen.";
      setFeedback({ type: "error", message });
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(t: FieldTemplate) {
    try {
      await api.put<FieldTemplate>(`/field-templates/${t.id}`, { is_active: !t.is_active });
      setFeedback({
        type: "success",
        message: t.is_active ? "Template deaktiviert." : "Template aktiviert.",
      });
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Aktion fehlgeschlagen.";
      setFeedback({ type: "error", message });
    }
  }

  async function duplicate(t: FieldTemplate) {
    const key = window.prompt("Template-Key für neue Version:", t.template_key) || "";
    if (!key.trim()) return;
    const versionRaw = window.prompt("Version für neue Variante:", "1");
    const version = Number(versionRaw || 1);
    if (!Number.isFinite(version) || version < 1) {
      setFeedback({ type: "error", message: "Version muss eine Zahl >= 1 sein." });
      return;
    }
    try {
      await api.post<FieldTemplate>(`/field-templates/${t.id}/duplicate`, {
        template_key: key.trim(),
        version,
      });
      setFeedback({ type: "success", message: "Template als neue Version angelegt." });
      await reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Duplizieren fehlgeschlagen.";
      setFeedback({ type: "error", message });
    }
  }

  return (
    <AppShell
      title="Feld-Templates verwalten"
      subtitle={
        <>
          Vorlagen für Stammdaten- und Wartungsformular-Felder. Scope, Template-Key und Label werden
          serverseitig automatisch bestimmt; hier wird nur Typ, Version und Status gepflegt.
        </>
      }
      back={{ to: "/admin", label: "zurück zum Admin-Hub" }}
    >
      <Card title="Neues Feld-Template anlegen">
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <SelectInput
              id="ft-field-type"
              label="Field Type"
              required
              value={form.field_type}
              onChange={(e) => setForm((s) => ({ ...s, field_type: e.target.value }))}
            >
              {fieldTypes.map((ft) => (
                <option key={ft.type} value={ft.type}>
                  {ft.type} – {ft.label}
                </option>
              ))}
            </SelectInput>
            <TextInput
              id="ft-version"
              label="Version"
              type="number"
              min={1}
              required
              value={form.version}
              onChange={(e) =>
                setForm((s) => ({ ...s, version: Number(e.target.value || 1) }))
              }
            />
            <div className="flex items-end">
              <Checkbox
                id="ft-active"
                label="Aktiv"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
              />
            </div>
          </div>
          <TextareaInput
            id="ft-description"
            label="Beschreibung (optional)"
            value={form.description}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={submitting}>
              Template speichern
            </Button>
            <Button type="button" variant="secondary" onClick={resetForm}>
              Zurücksetzen
            </Button>
          </div>
          {feedback && (
            <Alert
              variant={feedback.type === "warning" ? "info" : feedback.type}
              title={
                feedback.type === "success"
                  ? "Erledigt"
                  : feedback.type === "error"
                    ? "Fehler"
                    : "Hinweis"
              }
            >
              {feedback.message}
            </Alert>
          )}
        </form>
      </Card>

      <Card title="Vorhandene Templates">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Spinner />
            <span>Lädt …</span>
          </div>
        )}
        {!loading && loadError && (
          <Alert variant="error" title="Templates konnten nicht geladen werden">
            {loadError}
          </Alert>
        )}
        {!loading && !loadError && templates.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Keine Feld-Templates vorhanden.</p>
        )}
        {!loading && !loadError && templates.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((t) => (
              <article
                key={t.id}
                className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {t.default_label || t.template_key || "Unbenannt"}
                </h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  <code className="break-all rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] dark:bg-slate-800">
                    {t.template_key}
                  </code>{" "}
                  · v{t.version}
                </p>
                <dl className="mt-2 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                  <div>
                    <dt className="inline text-slate-400">Scope: </dt>
                    <dd className="inline font-semibold text-slate-700 dark:text-slate-200">
                      {t.scope}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline text-slate-400">Typ: </dt>
                    <dd className="inline font-semibold text-slate-700 dark:text-slate-200">
                      {t.field_type}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline text-slate-400">Status: </dt>
                    <dd
                      className={
                        t.is_active
                          ? "inline font-semibold text-emerald-700 dark:text-emerald-300"
                          : "inline font-semibold text-slate-500"
                      }
                    >
                      {t.is_active ? "Aktiv" : "Inaktiv"}
                    </dd>
                  </div>
                </dl>
                <div className="mt-auto flex flex-wrap gap-2 pt-3">
                  <Button size="sm" variant="secondary" onClick={() => duplicate(t)}>
                    Als neue Version duplizieren
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => toggleActive(t)}>
                    {t.is_active ? "Deaktivieren" : "Aktivieren"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => loadTemplateIntoForm(t)}>
                    Ins Formular laden
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>
    </AppShell>
  );
}
